import { NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { IconComponent } from "@components/icon/icon.component";
import { InputComponent } from "@components/input/input.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { uniq } from "lodash";
import { Subject } from "rxjs";
import { filter, map, takeUntil, tap } from "rxjs/operators";
import { IExternOrganisation } from "~app/common/models/kontakter/KontaktDTO";
import { ValidationMessageRowComponent } from "../../../../../../common/validation/validation-message-row/validation-message-row.component";

export type ExternOrganisationChangeEvent = {
 externOrganisationer: IExternOrganisation[];
    isValid: boolean;
};

export type ExternOrganisationForm = FormGroup<{
    namn: FormControl<string>;
    externKod: FormControl<string>;
    aktiv: FormControl<boolean>;
    removable: FormControl<boolean>;
}>;

export type ExternOrganisationerForm = FormGroup<{
    create: FormGroup<{
        namn: FormControl<string>;
        externKod: FormControl<string>;
    }>;
    externOrganisationer: FormArray<ExternOrganisationForm>;
}>;

@Component({
    selector: "rask-externa-organisationer",
    templateUrl: "./externa-organisationer.component.html",
    styleUrls: ["./externa-organisationer.component.scss"],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ValidationMessageRowComponent,
        InputComponent,
        ButtonComponent,
        IconComponent,
        NgClass,
        CheckboxComponent,
        IconButtonComponent,
        NgbTooltip
    ]
})
export class ExternaOrganisationerComponent implements OnInit, OnDestroy {
    @Input() externOrganisationer: IExternOrganisation[];
    @Output() externOrganisationerChange = new EventEmitter<ExternOrganisationChangeEvent>();

    protected form: ExternOrganisationerForm;

    private readonly destroySrc = new Subject<void>();
    private readonly formBuilder = inject(FormBuilder);

    protected get createLeverantorsOrganisationForm() {
        return this.form.controls.create;
    }

    protected get externOrganisationerFormArray() {
        return this.form.controls.externOrganisationer;
    }

    ngOnDestroy(): void {
        this.destroySrc.next();
        this.destroySrc.complete();
    }

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            create: this.formBuilder.group({
                namn: [
                    "",
                    [Validators.required]
                ],
                externKod: [
                    "",
                    [Validators.required]
                ]
            }),

            externOrganisationer: this.formBuilder.array<ExternOrganisationForm>([], [
                hasDuplicateExternKod,
                allValid
            ] )
        });

        this.externOrganisationerFormArray.valueChanges.pipe(
            filter(() => this.externOrganisationerFormArray.touched),
            map(() => this.form.controls.externOrganisationer.controls.map(fg => {
                const m: IExternOrganisation = {
                    namn: fg.controls.namn.value,
                    externKod: fg.controls.externKod.value,
                    aktiv: fg.controls.aktiv.value
                };
                return m;
            })),
            tap((eo) => this.externOrganisationerChange.emit({
                externOrganisationer: eo,
                isValid: this.externOrganisationerFormArray.status === "VALID"
            })),
            takeUntil(this.destroySrc)
        ).subscribe();

        this.externOrganisationer?.forEach(element => {
            this.addRow({
                ...element,
                removable: element.hasVirkeskopskontrakt !== true
            });
        });
    }

    protected createRow(): void {
        if(!this.createLeverantorsOrganisationForm.valid) {
            this.createLeverantorsOrganisationForm.markAllAsTouched();
            return;
        }

        this.externOrganisationerFormArray.markAllAsTouched();

        this.addRow({
            namn: this.createLeverantorsOrganisationForm.controls.namn.value,
            externKod: this.createLeverantorsOrganisationForm.controls.externKod.value,
            aktiv: true,
            removable: true
        });
    }

    protected getValidationErrors(): string[] {
        if(this.createLeverantorsOrganisationForm.untouched) {
            return[];
        }
        const  { externKod, namn } = this.createLeverantorsOrganisationForm.controls;
        const errors = [];
        if(namn.hasError("required") && namn.touched) {
            errors.push("Namn är obligatoriskt");
        }

        if(externKod.hasError("required") && externKod.touched) {
            errors.push("Kod är obligatoriskt");
        }

        return errors;
    }

    protected removeRow(index: number): void {
        this.externOrganisationerFormArray.removeAt(index);
    }

    private addRow(data: Partial<IExternOrganisation> & { removable: boolean }): void {
        const externOrganisationForm = this.formBuilder.group({
            namn: [
                data.namn,
                [Validators.required]
            ],
            externKod: [
                data.externKod,
                [Validators.required]
            ],
            aktiv: [data.aktiv],
            removable: [data.removable]
        });

        this.externOrganisationerFormArray.push(externOrganisationForm);
    }
}

export const allValid: ValidatorFn = (array: FormArray<ExternOrganisationForm>): ValidationErrors | null => {
    const invalid = array.controls.some(item => item.status === "INVALID");
    if(!invalid) {
        return null;
    }
    return {
        allValid: true
    };
};
export const hasDuplicateExternKod: ValidatorFn = (array: FormArray<ExternOrganisationForm>): ValidationErrors | null => {
    const koder = array.controls.map(x => x.controls.externKod.value);
    if(uniq(koder).length === koder.length) {
        return null;
    }
    return {
        duplicate: true
    };
};
